/// <reference types="vite/client" />
const BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Admin session token.
 *
 * This dashboard used to ship ADMIN_API_KEY inside the bundle via
 * VITE_ADMIN_API_KEY, which meant anyone who opened the site could read the
 * master key out of the JavaScript. Now the browser only ever holds a
 * short-lived token obtained by POSTing the operator password, and the API key
 * stays a machine credential that never leaves the server's environment.
 *
 * sessionStorage rather than localStorage: the session should not outlive the
 * tab, and it keeps the token out of other tabs on the same origin.
 */
import { frenchErrorMessage } from './errorMessages';

const TOKEN_KEY = 'staynow_admin_token';

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

/** Thrown when the session is missing or expired, so callers can show login. */
export class UnauthorizedError extends Error {
  constructor(message = 'Session expirée') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const isMutation = options?.method && options.method !== 'GET';
  const token = getToken();

  const res = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(isMutation ? { 'x-idempotency-key': crypto.randomUUID() } : {}),
      ...options?.headers,
    },
    ...options,
  });

  if (res.status === 401) {
    // The token is spent — drop it so the app falls back to the login screen
    // instead of retrying forever with a credential the server rejects.
    clearToken();
    throw new UnauthorizedError();
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const code = body?.error?.code as string | undefined;
    // Never surface the server's own `message`: the API answers in English
    // and this panel is French.
    throw new ApiError(frenchErrorMessage(code, res.status), res.status, code);
  }

  return res.json();
}

export async function login(password: string): Promise<void> {
  const res = await fetch(`${BASE}/admin/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-idempotency-key': crypto.randomUUID(),
    },
    body: JSON.stringify({ password }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // 429 is the throttler: say so plainly rather than "invalid password",
    // which would send the operator hunting for a typo that is not there.
    const message =
      res.status === 429
        ? 'Trop de tentatives. Réessayez dans une minute.'
        : 'Mot de passe invalide';
    throw new Error(message);
  }

  const body = (await res.json()) as { data: { token: string } };
  setToken(body.data.token);
}

export interface PendingOffer {
  id: string;
  title: string;
  description: string;
  photo_url: string | null;
  location_label: string;
  reference_price: number;
  starts_at: string;
  expires_at: string;
  checkout_at: string;
  host_id: string;
  created_at: string;
}

export interface ActiveOffer {
  id: string;
  title: string;
  description: string | null;
  photo_url: string | null;
  location_label: string | null;
  current_price: number;
  reference_price: number;
  currency: 'CAD';
  starts_at: string;
  expires_at: string;
  checkout_at: string;
  time_remaining_seconds: number;
  host_id: string | null;
  activated_at: string | null;
  source_type: 'HOTE' | 'PARTICULIER';
  pricing_version: string;
}

export interface ActiveOffersResponse {
  server_time: string;
  data: ActiveOffer[];
  has_more: boolean;
  next_cursor: string | null;
}

/** Mirrors `HostAccommodationType` in server/src/common/enums. */
export type HostAccommodationType =
  | 'hotel'
  | 'short_term_rental'
  | 'bnb'
  | 'camping'
  | 'chalet'
  | 'other';

export interface PendingHost {
  id: string;
  display_name: string | null;
  email: string;
  created_at: string;
  /** When the host role was requested — not when the account was created. */
  requested_at: string;
  /** Null for accounts that applied before the application form existed. */
  accommodation_type: HostAccommodationType | null;
  listing_url: string | null;
}

export interface PendingCession {
  id: string;
  title: string;
  description: string;
  proof_document_url: string | null;
  location_label: string;
  reference_price: number;
  proof_original_price: number;
  reference_url: string | null;
  starts_at: string;
  expires_at: string;
  checkout_at: string;
  host_id: string;
  created_at: string;
}

export interface KpiData {
  live_offers_count: number;
  saved_percentage: number;
  avg_time_to_booking_seconds: number | null;
  avg_discount_applied: number | null;
  expired_vs_reserved_ratio: number | null;
  generated_at: string;
}

/** SRS 6.2.4 — see docs/SIGNAL_LIVE_GOVERNANCE.md for when to use these. */
export interface SignalIntegrityState {
  publication_suspended: boolean;
  feed_suspended: boolean;
  reason: string | null;
  actor: string | null;
  /** Null when nobody has ever touched the switches. */
  changed_at: string | null;
}

export interface SurveyBarrierCount {
  id: string;
  label: string;
  count: number;
}

export interface SurveyRoleSummary {
  count: number;
  likelihood_avg: number | null;
  price_clarity_avg: number | null;
  barriers: SurveyBarrierCount[];
}

export interface SurveySummary {
  survey_key: string;
  total: number;
  contact_ok: number;
  voyageur: SurveyRoleSummary;
  hote: SurveyRoleSummary;
}

export interface SurveyResponseRow {
  id: string;
  role: 'voyageur' | 'hote';
  locale: string;
  app_version: string | null;
  os_version: string | null;
  has_booked: boolean;
  has_published: boolean;
  answers: {
    q1_likelihood: number;
    q2_price_clarity: number;
    q3_barriers: { choices: string[]; other: string | null };
    q4_friction: string | null;
    q5_one_change: string | null;
  };
  contact_ok: boolean;
  /** Only present when the tester agreed to be contacted. */
  contact: { name: string | null; email: string | null } | null;
  created_at: string;
  updated_at: string;
}

export interface SurveyResponsesPage {
  data: SurveyResponseRow[];
  /** Only on the first page. */
  summary?: SurveySummary;
  has_more: boolean;
  next_cursor: string | null;
}

export const api = {
  getActiveOffers: (cursor?: string, limit = 10) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', cursor);
    return request<ActiveOffersResponse>(`/admin/offers/active?${params.toString()}`);
  },

  getPendingOffers: () =>
    request<{ data: PendingOffer[] }>('/admin/offers/pending'),

  approveOffer: (id: string) =>
    request<{ data: unknown }>(`/admin/offers/${id}/approve`, { method: 'POST' }),

  refuseOffer: (id: string, reason: string) =>
    request<{ data: unknown }>(`/admin/offers/${id}/refuse`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  killOffer: (id: string) =>
    request<{ data: unknown }>(`/admin/offers/${id}/kill`, { method: 'POST' }),

  getPendingHosts: () =>
    request<{ data: PendingHost[] }>('/admin/users/pending-hote'),

  validateHost: (id: string) =>
    request<{ data: unknown }>(`/admin/users/${id}/validate-hote`, { method: 'POST' }),

  getPendingCessions: () =>
    request<{ data: PendingCession[] }>('/admin/cessions/pending'),

  approveCession: (id: string) =>
    request<{ data: unknown }>(`/admin/cessions/${id}/approve`, { method: 'POST' }),

  refuseCession: (id: string, reason: string) =>
    request<{ data: unknown }>(`/admin/cessions/${id}/refuse`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  getKpi: () =>
    request<{ data: KpiData }>('/admin/kpi'),

  /**
   * `amount` in cents, omitted for a full refund. The server reverses the
   * owner's payout first where one was made — refunding alone would pay them
   * out of Staynow's own balance.
   */
  refundBooking: (id: string, reason: string, amount?: number) =>
    request<{ data: { booking_id: string; refunded_amount: number } }>(
      `/admin/bookings/${id}/refund`,
      {
        method: 'POST',
        body: JSON.stringify(amount === undefined ? { reason } : { reason, amount }),
      },
    ),

  getSurveyResponses: (cursor?: string) => {
    const params = new URLSearchParams({ limit: '50' });
    if (cursor) params.set('cursor', cursor);
    return request<SurveyResponsesPage>(
      `/admin/surveys/beta/responses?${params.toString()}`,
    );
  },

  getSignalIntegrity: () =>
    request<{ data: SignalIntegrityState }>('/admin/signal-integrity'),

  /**
   * Both switches are sent on every call: the server stores each change as a
   * complete state, so a partial update would silently clear the other one.
   */
  setSignalIntegrity: (input: {
    publication_suspended: boolean;
    feed_suspended: boolean;
    reason?: string;
  }) =>
    request<{ data: SignalIntegrityState }>('/admin/signal-integrity', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};
